// Client-side image preparation: optional resize + JPEG re-encode with progress.
// Returns a base64 data URL ready to send to the server.

export type PrepareProgress = {
  phase: "reading" | "decoding" | "resizing" | "encoding" | "done";
  percent: number; // 0..100
};

export type PrepareResult = {
  dataUrl: string;
  originalBytes: number;
  compressedBytes: number;
  width: number;
  height: number;
  resized: boolean;
  recompressed: boolean;
};

export type PrepareOptions = {
  maxDimension?: number; // longest side in px (default 2048)
  quality?: number;      // JPEG quality 0..1 (default 0.82)
  // Skip recompression when file is already small AND already a JPEG/webp under this size.
  skipBytesThreshold?: number; // default 600 KB
};

function readAsDataUrl(file: Blob, onProgress?: (pct: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress((e.loaded / e.total) * 100);
    };
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode failed"));
    img.src = src;
  });
}

function approxBytesFromDataUrl(dataUrl: string): number {
  const i = dataUrl.indexOf(",");
  const b64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
  // Subtract padding to get a closer estimate.
  const padding = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}

export async function prepareImage(
  file: File,
  opts: PrepareOptions = {},
  onProgress?: (p: PrepareProgress) => void,
): Promise<PrepareResult> {
  const maxDim = opts.maxDimension ?? 2048;
  const quality = opts.quality ?? 0.82;
  const skipThreshold = opts.skipBytesThreshold ?? 600 * 1024;

  const emit = (phase: PrepareProgress["phase"], percent: number) =>
    onProgress?.({ phase, percent: Math.max(0, Math.min(100, percent)) });

  emit("reading", 0);
  const originalBytes = file.size;

  // Read original to a data URL so we can both decode and (optionally) reuse it as-is.
  const originalDataUrl = await readAsDataUrl(file, (pct) => emit("reading", pct * 0.5));
  emit("decoding", 55);

  const img = await loadImage(originalDataUrl);
  const { naturalWidth: w, naturalHeight: h } = img;
  const longest = Math.max(w, h);
  const needsResize = longest > maxDim;
  const isCompressible = /^image\/(jpeg|jpg|webp|png)$/i.test(file.type || "");
  const canSkip =
    !needsResize &&
    isCompressible &&
    originalBytes <= skipThreshold;

  if (canSkip) {
    emit("done", 100);
    return {
      dataUrl: originalDataUrl,
      originalBytes,
      compressedBytes: originalBytes,
      width: w,
      height: h,
      resized: false,
      recompressed: false,
    };
  }

  emit("resizing", 65);
  const scale = needsResize ? maxDim / longest : 1;
  const targetW = Math.round(w * scale);
  const targetH = Math.round(h * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas 2d unavailable");
  // White background so JPEG doesn't render transparency as black.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(img, 0, 0, targetW, targetH);

  emit("encoding", 85);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality),
  );
  if (!blob) throw new Error("encode failed");

  const dataUrl = await readAsDataUrl(blob, (pct) => emit("encoding", 85 + pct * 0.14));
  emit("done", 100);

  return {
    dataUrl,
    originalBytes,
    compressedBytes: approxBytesFromDataUrl(dataUrl),
    width: targetW,
    height: targetH,
    resized: needsResize,
    recompressed: true,
  };
}

export function formatBytesShort(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
